import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { RetentionCohort } from '@/services/admin-api';

interface RevenueChartProps {
  revenueMetrics: {
    data: {
      revenuePerDay: Array<{ date: string; total: number }>;
      totalRevenue: number;
    };
  } | undefined;
  canViewRevenue: boolean;
  retentionCohorts: {
    data: RetentionCohort[];
  } | undefined;
}

export function RevenueChart({ revenueMetrics, canViewRevenue, retentionCohorts }: RevenueChartProps) {
  return (
    <>
      {canViewRevenue && (
        <Card>
          <CardHeader>
            <CardTitle>Revenue</CardTitle>
            <CardDescription>
              Total revenue: ${revenueMetrics?.data.totalRevenue.toFixed(2)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenueMetrics?.data.revenuePerDay || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(date) => new Date(date).toLocaleDateString()} />
                  <YAxis tickFormatter={(value) => `$${value}`} />
                  <Tooltip
                    labelFormatter={(date) => new Date(date).toLocaleDateString()}
                    formatter={(value: number) => [`$${value.toFixed(2)}`, 'Revenue']}
                  />
                  <Line type="monotone" dataKey="total" stroke="#22c55e" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Retention Cohorts</CardTitle>
          <CardDescription>Week-over-week user retention rates (%)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="py-2 text-left font-medium">Cohort Week</th>
                  <th className="py-2 text-left font-medium">Users</th>
                  {[...Array(8)].map((_, i) => (
                    <th key={i} className="py-2 text-center font-medium">Week {i}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {retentionCohorts?.data.map((cohort) => (
                  <tr key={cohort.cohortWeek} className="border-b">
                    <td className="py-2">{cohort.cohortWeek}</td>
                    <td className="py-2">{cohort.totalUsers}</td>
                    {cohort.retainedByWeek.map((rate, i) => (
                      <td
                        key={i}
                        className="py-2 text-center"
                        style={{
                          backgroundColor: `rgba(34, 197, 94, ${rate / 100 * 0.5})`,
                        }}
                      >
                        {rate}%
                      </td>
                    ))}
                    {[...Array(8 - cohort.retainedByWeek.length)].map((_, i) => (
                      <td key={`empty-${i}`} className="py-2 text-center text-muted-foreground">
                        -
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
